import React, { createContext, useContext, useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';

const AuthContext = createContext({})

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider')
  }
  return context
}

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null)
  const [userProfile, setUserProfile] = useState(null)
  const [loading, setLoading] = useState(true)
  const [profileLoading, setProfileLoading] = useState(false)
  const [sessionChecked, setSessionChecked] = useState(false)
  
  // Check if debug mode is enabled
  const isDebugMode = import.meta.env?.VITE_DEBUG_AUTH === 'true';
  
  // DEBUG: Session state tracking (only when debug enabled)
  const [debugSession, setDebugSession] = useState({
    hasSession: false,
    userId: null,
    sessionExpiresAt: null,
    lastEvent: null,
    lastEventTime: null
  })

  // DEBUG: Extract and log Supabase project hostname (only when debug enabled)
  useEffect(() => {
    if (!isDebugMode) return;
    
    const supabaseUrl = import.meta.env?.VITE_SUPABASE_URL;
    if (supabaseUrl) {
      try {
        const url = new URL(supabaseUrl);
        const hostname = url?.hostname;
        console.log('🔍 DEBUG - Supabase Project:', hostname);
        
        // Add temporary visual debug banner
        const debugBanner = document.createElement('div');
        debugBanner.id = 'supabase-debug-banner';
        debugBanner.style.cssText = `
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          background: #4F46E5;
          color: white;
          padding: 8px 16px;
          text-align: center;
          font-size: 14px;
          font-weight: 600;
          z-index: 9999;
          box-shadow: 0 2px 4px rgba(0,0,0,0.1);
        `;
        debugBanner.textContent = `🔍 DEBUG MODE - Supabase Project: ${hostname}`;
        document.body?.prepend(debugBanner);
        
        // Cleanup on unmount
        return () => {
          const banner = document.getElementById('supabase-debug-banner');
          if (banner) banner?.remove();
        };
      } catch (error) {
        console.error('Failed to parse Supabase URL:', error);
      }
    }
  }, [isDebugMode]);

  // DEBUG: Session persistence testing (only when debug enabled)
  useEffect(() => {
    if (!isDebugMode) return;
    
    console.log('🔍 DEBUG - AuthContext mounted, checking session...');
    
    // Test 1: Initial session check
    supabase?.auth?.getSession()?.then(({ data: { session }, error }) => {
      console.log('🔍 DEBUG - Initial getSession() result:', {
        hasSession: !!session,
        userId: session?.user?.id || null,
        expiresAt: session?.expires_at || null,
        error: error || null
      });
      
      setDebugSession(prev => ({
        ...prev,
        hasSession: !!session,
        userId: session?.user?.id || null,
        sessionExpiresAt: session?.expires_at || null,
        lastEvent: 'INITIAL_CHECK',
        lastEventTime: new Date()?.toISOString()
      }));
    });
  }, [isDebugMode]);

  // Isolated async operations - never called from auth callbacks
  const profileOperations = {
    async load(userId) {
      if (!userId) {
        // ✅ CRITICAL: Always set sessionChecked even if no userId
        setSessionChecked(true)
        return
      }
      setProfileLoading(true)
      try {
        const { data, error } = await supabase?.from('user_profiles')?.select('*')?.eq('id', userId)?.single()
        
        // ✅ SESSION AUTO-REPAIR: If profile doesn't exist but we have valid session, auto-create profile
        if (error && error?.code === 'PGRST116') { // PGRST116 = row not found
          console.log('[AuthContext] Profile not found, auto-repairing session for user:', userId);
          
          const { data: session } = await supabase?.auth?.getSession();
          const user = session?.session?.user;
          
          if (user) {
            const { error: upsertError } = await supabase?.from('user_profiles')?.upsert({
              id: userId,
              email: user?.email,
              full_name: user?.user_metadata?.full_name || user?.email?.split('@')?.[0],
              role: user?.user_metadata?.role || 'customer'
            });
            
            if (!upsertError) {
              console.log('[AuthContext] Session auto-repair successful');
              // Fetch the newly created profile
              const { data: newProfile } = await supabase?.from('user_profiles')?.select('*')?.eq('id', userId)?.single();
              if (newProfile) {
                setUserProfile(newProfile);
              }
            } else {
              console.error('[AuthContext] Error during session auto-repair:', upsertError);
            }
          }
        } else if (!error) {
          setUserProfile(data)
        }
      } catch (error) {
        console.error('Profile load error:', error)
      } finally {
        setProfileLoading(false)
        // ✅ CRITICAL: Set sessionChecked after profile load attempt
        setSessionChecked(true)
      }
    },

    clear() {
      setUserProfile(null)
      setProfileLoading(false)
      // ✅ CRITICAL: Set sessionChecked when clearing profile
      setSessionChecked(true)
    }
  }

  // Auth state handlers - PROTECTED from async modification
  const authStateHandlers = {
    // This handler MUST remain synchronous - Supabase requirement
    onChange: (event, session) => {
      if (isDebugMode) {
        console.log('🔍 DEBUG - Auth state change:', {
          event,
          hasSession: !!session,
          userId: session?.user?.id || null,
          timestamp: new Date()?.toISOString()
        });
      }
      
      setUser(session?.user ?? null)
      setLoading(false)
      
      // Update debug session state (only when debug enabled)
      if (isDebugMode) {
        setDebugSession(prev => ({
          ...prev,
          hasSession: !!session,
          userId: session?.user?.id || null,
          sessionExpiresAt: session?.expires_at || null,
          lastEvent: event,
          lastEventTime: new Date()?.toISOString()
        }));
      }
      
      if (session?.user) {
        profileOperations?.load(session?.user?.id) // Fire-and-forget
      } else {
        profileOperations?.clear()
      }
    }
  }

  useEffect(() => {
    // ✅ IMPROVED: Initial session check with proper error handling
    let timeoutId;
    let isResolved = false;
    
    // Set a timeout fallback to ensure sessionChecked is set even if Supabase is slow
    timeoutId = setTimeout(() => {
      if (!isResolved) {
        console.warn('[AuthContext] Session check timeout - setting sessionChecked to true');
        setLoading(false);
        setSessionChecked(true);
      }
    }, 5000); // 5 second timeout

    supabase?.auth?.getSession()?.then(({ data: { session }, error }) => {
      isResolved = true;
      clearTimeout(timeoutId);
      if (error) {
        console.error('Initial session check error:', error)
        setLoading(false)
        setSessionChecked(true)
        return
      }
      authStateHandlers?.onChange('INITIAL_SESSION', session)
    })?.catch((error) => {
      isResolved = true;
      clearTimeout(timeoutId);
      console.error('Session check failed:', error)
      setLoading(false)
      setSessionChecked(true)
    })

    // CRITICAL: This must remain synchronous
    const { data: { subscription } } = supabase?.auth?.onAuthStateChange(
      authStateHandlers?.onChange
    )

    return () => {
      clearTimeout(timeoutId);
      subscription?.unsubscribe();
    }
  }, [])

  // DEBUG: Force refresh session function (only when debug enabled)
  const forceRefreshSession = async () => {
    if (!isDebugMode) return;
    
    console.log('🔍 DEBUG - Force refresh initiated...');
    
    // Test getSession()
    const { data: sessionData, error: sessionError } = await supabase?.auth?.getSession();
    console.log('🔍 DEBUG - getSession() result:', {
      hasSession: !!sessionData?.session,
      userId: sessionData?.session?.user?.id || null,
      expiresAt: sessionData?.session?.expires_at || null,
      error: sessionError || null
    });
    
    // Test refreshSession()
    const { data: refreshData, error: refreshError } = await supabase?.auth?.refreshSession();
    console.log('🔍 DEBUG - refreshSession() result:', {
      hasSession: !!refreshData?.session,
      userId: refreshData?.session?.user?.id || null,
      expiresAt: refreshData?.session?.expires_at || null,
      error: refreshError || null
    });
    
    // Update debug state
    setDebugSession(prev => ({
      ...prev,
      hasSession: !!refreshData?.session,
      userId: refreshData?.session?.user?.id || null,
      sessionExpiresAt: refreshData?.session?.expires_at || null,
      lastEvent: 'FORCE_REFRESH',
      lastEventTime: new Date()?.toISOString()
    }));
  };

  // Auth methods
  const signIn = async (email, password) => {
    try {
      const { data, error } = await supabase?.auth?.signInWithPassword({ email, password })
      return { data, error }
    } catch (error) {
      return { error: { message: 'Network error. Please try again.' } }
    }
  }

  const signUp = async (email, password, metadata = {}) => {
    try {
      const { data, error } = await supabase?.auth?.signUp({
        email,
        password,
        options: {
          data: {
            full_name: metadata?.full_name || '',
            role: metadata?.role || 'customer',
            phone: metadata?.phone || '',
            company_name: metadata?.company_name || '',
            avatar_url: metadata?.avatar_url || ''
          }
        }
      })
      
      // If signup successful but no session (email confirmation required)
      if (data?.user && !data?.session) {
        return {
          data,
          error: null,
          requiresEmailConfirmation: true
        }
      }
      
      return { data, error, requiresEmailConfirmation: false }
    } catch (error) {
      return { error: { message: 'Network error. Please try again.' } }
    }
  }

  const signOut = async () => {
    try {
      const { error } = await supabase?.auth?.signOut()
      if (!error) {
        setUser(null)
        profileOperations?.clear()
      }
      return { error }
    } catch (error) {
      return { error: { message: 'Network error. Please try again.' } }
    }
  }

  const updateProfile = async (updates) => {
    if (!user) return { error: { message: 'No user logged in' } }
    
    try {
      const { data, error } = await supabase?.from('user_profiles')?.update(updates)?.eq('id', user?.id)?.select()?.single()
      if (!error) setUserProfile(data)
      return { data, error }
    } catch (error) {
      return { error: { message: 'Network error. Please try again.' } }
    }
  }

  const value = {
    user,
    userProfile,
    loading,
    profileLoading,
    sessionChecked,
    signIn,
    signUp,
    signOut,
    updateProfile,
    isAuthenticated: !!user,
    // DEBUG: Expose debug values only when debug enabled
    ...(isDebugMode && {
      debugSession,
      forceRefreshSession
    })
  }

  return (
    <AuthContext.Provider value={value}>
      {/* DEBUG: On-screen debug panel (only when debug enabled) */}
      {isDebugMode && (
        <div style={{
          position: 'fixed',
          bottom: '20px',
          right: '20px',
          background: '#1F2937',
          color: '#F3F4F6',
          padding: '16px',
          borderRadius: '8px',
          boxShadow: '0 4px 6px rgba(0,0,0,0.3)',
          zIndex: 9998,
          fontSize: '12px',
          maxWidth: '300px',
          fontFamily: 'monospace'
        }}>
          <div style={{ fontWeight: 'bold', marginBottom: '8px', color: '#60A5FA' }}>
            🔍 Session Debug Panel
          </div>
          <div style={{ marginBottom: '4px' }}>
            <strong>sessionChecked:</strong> {sessionChecked ? '✅ true' : '❌ false'}
          </div>
          <div style={{ marginBottom: '4px' }}>
            <strong>authLoading:</strong> {loading ? '⏳ true' : '✅ false'}
          </div>
          <div style={{ marginBottom: '4px' }}>
            <strong>userId:</strong> {debugSession?.userId || '❌ null'}
          </div>
          <div style={{ marginBottom: '4px' }}>
            <strong>hasSession:</strong> {debugSession?.hasSession ? '✅ true' : '❌ false'}
          </div>
          <div style={{ marginBottom: '4px' }}>
            <strong>sessionExpiresAt:</strong> {debugSession?.sessionExpiresAt ? new Date(debugSession?.sessionExpiresAt * 1000)?.toLocaleString() : '❌ null'}
          </div>
          <div style={{ marginBottom: '4px' }}>
            <strong>lastEvent:</strong> {debugSession?.lastEvent || 'none'}
          </div>
          <button
            onClick={forceRefreshSession}
            style={{
              marginTop: '12px',
              width: '100%',
              padding: '8px',
              background: '#3B82F6',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontWeight: '600',
              fontSize: '12px'
            }}
            onMouseOver={(e) => e.target.style.background = '#2563EB'}
            onMouseOut={(e) => e.target.style.background = '#3B82F6'}
          >
            🔄 Force Refresh Session
          </button>
        </div>
      )}
      {children}
    </AuthContext.Provider>
  );
}